import { useEffect } from 'react';
import { customerAPI } from '@matix/api-client';
import { phoneMatchesCaller, takeCustomerCallPhone } from '../../lib/customerCallLink';
import { useCart } from '../../store/cart';
import { useCustomerPicker } from '../../store/customers';
import { useUI } from '../../store/ui';

/**
 * Acts on the phone-system link (lib/customerCallLink) once the till is up:
 *   - a cash customer with the caller's number → their customer screen, and
 *     attached to the cart when the cart is empty (a sale in progress for
 *     someone else is never re-assigned by a ringing phone);
 *   - no such customer → the new-customer form with the number typed in.
 * The old POS did the same from `/Order/customerCall?phoneNo=…`.
 *
 * No cancellation on cleanup: the number is taken exactly once, so a
 * StrictMode re-run must not abandon the lookup it started.
 */
export function useCustomerCall(ready: boolean): void {
  useEffect(() => {
    if (!ready) return;
    const phone = takeCustomerCallPhone();
    if (phone) void openCaller(phone);
  }, [ready]);
}

async function openCaller(phone: string): Promise<void> {
  // The search matches digits anywhere in the stored phone; the last 8 also
  // find a customer saved without the country code the PBX sent.
  const q = phone.length > 8 ? phone.slice(-8) : phone;
  let matchId: number | null = null;
  try {
    const res = await customerAPI.search(q, 'cash', 50);
    matchId = res.customers.find((c) => phoneMatchesCaller(c.phone, phone))?.id ?? null;
  } catch {
    /* search failed — offer the new-customer form; its duplicate check still guards the phone */
  }

  if (matchId != null) {
    const cart = useCart.getState();
    if (cart.lines.length === 0 && cart.customer == null) {
      try {
        cart.setCustomer(await customerAPI.getById(matchId, 'cash'));
      } catch {
        /* still shown below; the cashier can attach it by hand */
      }
    }
    useCustomerPicker.getState().seedNextOpen({ searchMode: 'phone', searchText: q, selectedId: matchId });
  } else {
    useCustomerPicker.getState().seedNextOpen({ view: 'add', searchMode: 'phone', searchText: phone });
  }
  const ui = useUI.getState();
  ui.setTab('walkin');
  ui.showModal('customerPicker');
}
